import React, { useState } from 'react';
import { Box, Chip, IconButton, Stack, Typography } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import { Weapon } from '../../types';
import { PopoverItem } from '../../hooks/useItemPopover';
import { formatDamageModifier } from '../../utils/formatting';
import { SelectWeaponModal } from '../modals/SelectWeaponModal';
import { ConfirmActionDialog } from './ConfirmActionDialog';
import { editButtonOffsetSx } from '../../App';

interface MemberWeaponsProps {
  weapons: Weapon[];
  editMode: boolean;
  memberRole: 'wizard' | 'apprentice' | 'soldier';
  memberId: string;
  memberName: string;
  onItemClick: (event: React.MouseEvent<HTMLElement>, item: PopoverItem) => void;
  onSaveWeapons: (weaponIds: string[]) => void;
}

export const MemberWeapons: React.FC<MemberWeaponsProps> = ({
  weapons,
  editMode,
  memberRole,
  memberId,
  memberName,
  onItemClick,
  onSaveWeapons,
}) => {
  const [isAddWeaponOpen, setIsAddWeaponOpen] = useState(false);
  const [weaponPendingDelete, setWeaponPendingDelete] = useState<{ weapon: Weapon; idx: number } | null>(null);
  const canEdit = editMode && memberRole !== 'soldier';

  return (
    <Box sx={{ mb: 4 }}>
      <Box
        sx={{
          mb: 1,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          gap: 1,
        }}
      >
        <Stack direction="column" spacing={1} alignItems="flex-start">
          <Stack direction="row" spacing={1} alignItems="flex-start">
            <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
              Weapons
            </Typography>
            {canEdit && (
              <IconButton size="small" sx={editButtonOffsetSx} onClick={() => setIsAddWeaponOpen(true)}>
                <AddIcon />
              </IconButton>
            )}
          </Stack>
          {weapons.length ? (
            <Stack direction="column" spacing={1} alignItems="flex-start">
              {weapons.map((weapon, idx) => (
                <Chip
                  key={weapon.id}
                  size="small"
                  label={formatDamageModifier(weapon.name, weapon.damageModifier)}
                  onClick={(event) => onItemClick(event, weapon)}
                  onDelete={canEdit ? () => setWeaponPendingDelete({ weapon, idx }) : undefined}
                  sx={{ overflow: 'hidden' }}
                />
              ))}
            </Stack>
          ) : (
            <Typography variant="body2" color="textSecondary">
              None
            </Typography>
          )}
        </Stack>
      </Box>

      {canEdit && (
        <SelectWeaponModal
          open={isAddWeaponOpen}
          memberName={memberName}
          excludeWeaponIds={weapons.map((w) => w.id)}
          onClose={() => setIsAddWeaponOpen(false)}
          onSelect={(weapon) => {
            onSaveWeapons([...weapons.map((w) => w.id), weapon.id]);
            setIsAddWeaponOpen(false);
          }}
        />
      )}

      <ConfirmActionDialog
        open={weaponPendingDelete !== null}
        title="Remove weapon"
        message={weaponPendingDelete ? `Remove "${weaponPendingDelete.weapon.name}"?` : ''}
        confirmLabel="Remove"
        confirmColor="error"
        onClose={() => setWeaponPendingDelete(null)}
        onConfirm={() => {
          if (!weaponPendingDelete) return;
          onSaveWeapons(weapons.filter((_, i) => i !== weaponPendingDelete.idx).map((w) => w.id));
          setWeaponPendingDelete(null);
        }}
      />
    </Box>
  );
};
